/**
 * White-glove credentials portal: token-gated form per platform.
 * Plan: billing_infrastructure_plan §1.8. /onboarding/credentials/:token.
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  WHITE_GLOVE_PLATFORMS,
  isUuid,
  isTokenExpired,
  validateWhiteGloveCredentials,
} from '@/lib/white-glove';

export interface WhiteGloveCredentialsProps {
  /** For tests: override token validation (fetch org by token). */
  getTokenStatus?: (token: string) => Promise<{
    valid: boolean;
    expired?: boolean;
    platforms?: string[];
  }>;
  onSubmit?: (token: string, credentials: Record<string, { username: string; password: string }>) => Promise<void>;
}

export default function WhiteGloveCredentials({
  getTokenStatus,
  onSubmit: onSubmitProp,
}: WhiteGloveCredentialsProps = {}) {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'valid' | 'expired' | 'invalid'>('loading');
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [credentials, setCredentials] = useState<Record<string, { username: string; password: string }>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }
    if (!isUuid(token)) {
      setStatus('invalid');
      return;
    }
    if (getTokenStatus) {
      getTokenStatus(token).then((result) => {
        if (!result.valid) {
          setStatus(result.expired ? 'expired' : 'invalid');
          return;
        }
        setPlatforms(result.platforms ?? WHITE_GLOVE_PLATFORMS.map((p) => p.id));
        setStatus('valid');
      }).catch(() => setStatus('invalid'));
    } else {
      setPlatforms(WHITE_GLOVE_PLATFORMS.map((p) => p.id));
      setStatus('valid');
    }
  }, [token, getTokenStatus]);

  const handleCredentialChange = (platform: string, field: 'username' | 'password', value: string) => {
    setCredentials((prev) => ({
      ...prev,
      [platform]: { ...prev[platform], [field]: value },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);
    setSubmitError(null);
    const err = validateWhiteGloveCredentials(platforms, credentials);
    if (err) {
      setValidationError(err);
      return;
    }
    if (onSubmitProp && token) {
      try {
        await onSubmitProp(token, credentials);
        navigate('/onboarding/credentials/received');
      } catch (e) {
        setSubmitError(e instanceof Error ? e.message : 'Submit failed');
      }
    }
  };

  if (status === 'loading') {
    return <p data-testid="credentials-loading">Loading...</p>;
  }

  if (status === 'invalid') {
    return (
      <Card className="max-w-md mx-auto">
        <CardContent className="pt-6">
          <p data-testid="invalid-link-message">Invalid link</p>
          <Button variant="outline" onClick={() => navigate('/login')}>
            Go to login
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (status === 'expired') {
    return (
      <Card className="max-w-md mx-auto">
        <CardContent className="pt-6">
          <p data-testid="link-expired-message">Link expired</p>
          <Button data-testid="btn-request-new-link">Request New Link</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="max-w-md mx-auto" data-testid="credentials-form">
      <CardHeader>
        <CardTitle>Enter credentials</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {platforms.map((platformId) => {
            const platform = WHITE_GLOVE_PLATFORMS.find((p) => p.id === platformId);
            const label = platform?.label ?? platformId;
            return (
              <div key={platformId} className="space-y-2">
                <Label>{label}</Label>
                <Input
                  data-testid={`input-${platformId}-username`}
                  placeholder="Username"
                  value={credentials[platformId]?.username ?? ''}
                  onChange={(e) =>
                    handleCredentialChange(platformId, 'username', e.target.value)
                  }
                />
                <Input
                  data-testid={`input-${platformId}-password`}
                  type="password"
                  placeholder="Password"
                  value={credentials[platformId]?.password ?? ''}
                  onChange={(e) =>
                    handleCredentialChange(platformId, 'password', e.target.value)
                  }
                />
              </div>
            );
          })}
          {validationError && (
            <p className="text-sm text-destructive" data-testid="error-credentials">
              {validationError}
            </p>
          )}
          {submitError && (
            <p className="text-sm text-destructive">{submitError}</p>
          )}
          <Button type="submit" data-testid="btn-credentials-submit">
            Submit
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
